import { useEffect, useState } from "react";
import { Download, Check, Share, Plus, MoreVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export default function InstallButton({ variant = "sidebar", onNavigate }) {
  const [canInstall, setCanInstall] = useState(!!window.__bipEvent);
  const [installed, setInstalled] = useState(isStandalone());
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const ready = () => setCanInstall(true);
    const done = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("bip-ready", ready);
    window.addEventListener("bip-installed", done);
    return () => {
      window.removeEventListener("bip-ready", ready);
      window.removeEventListener("bip-installed", done);
    };
  }, []);

  const handleInstall = async () => {
    onNavigate?.();
    const evt = window.__bipEvent;
    if (evt) {
      evt.prompt();
      const choice = await evt.userChoice;
      if (choice?.outcome === "accepted") {
        setInstalled(true);
        setCanInstall(false);
      }
      window.__bipEvent = null;
    } else {
      setShowHelp(true);
    }
  };

  if (installed) {
    return (
      <div data-testid="install-installed" className="flex items-center justify-center gap-2 border-2 border-accent/60 text-accent px-4 py-3 text-xs overline font-bold">
        <Check size={16} /> App Installed
      </div>
    );
  }

  const base = "w-full flex items-center justify-center gap-2 px-4 py-3 text-xs overline font-bold transition-colors";
  const style =
    variant === "sidebar"
      ? `${base} bg-accent text-black hover:bg-white`
      : `${base} bg-accent text-black hover:bg-white`;

  return (
    <>
      <button data-testid="install-app-btn" onClick={handleInstall} className={style}>
        <Download size={16} /> Install App
      </button>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent data-testid="install-help-dialog" className="bg-card border-2 border-primary rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Install / Sideload FTO</DialogTitle>
            <DialogDescription className="text-muted-foreground">Add FTO to your device's home screen for full-screen, app-like listening.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 text-sm">
            <div>
              <div className="overline text-xs text-accent mb-2">iPhone / iPad (Safari)</div>
              <p className="text-muted-foreground flex items-center gap-2 flex-wrap">
                Tap <Share size={16} className="inline text-white" /> Share, then <Plus size={16} className="inline text-white" /> <span className="text-white">Add to Home Screen</span>.
              </p>
            </div>
            <div>
              <div className="overline text-xs text-accent mb-2">Android (Chrome)</div>
              <p className="text-muted-foreground flex items-center gap-2 flex-wrap">
                Tap <MoreVertical size={16} className="inline text-white" /> menu, then <span className="text-white">Install app / Add to Home screen</span>.
              </p>
            </div>
            <div>
              <div className="overline text-xs text-accent mb-2">Desktop (Chrome / Edge)</div>
              <p className="text-muted-foreground">
                Click the <span className="text-white">install icon</span> in the address bar (right side), or browser menu → <span className="text-white">Install FTO</span>.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
